import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2, Mail } from "lucide-react";
import { Header } from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailDelivered, setEmailDelivered] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in every field.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/contact", form, { withCredentials: true });
      setSent(true);
      setEmailDelivered(r.data?.email_sent);
      toast.success(
        r.data?.email_sent
          ? "Message sent — we'll get back to you soon."
          : "Message received — we'll get back to you soon."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-2xl mx-auto w-full px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Support</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Contact us</h1>
            <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed max-w-md">
              Bug reports, feature requests, billing questions — anything. We read every message.
            </p>
          </div>
          <Link
            to="/"
            data-testid="contact-back-btn"
            className="btn-outline flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        {sent ? (
          <div className="panel p-6 flex flex-col items-start gap-3" data-testid="contact-success">
            <div className="flex items-center gap-2 text-[#00E676]">
              <CheckCircle2 size={20} />
              <span className="text-base font-semibold tracking-tight">Message received</span>
            </div>
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              Thanks {form.name.split(" ")[0]} — your message has been saved
              {emailDelivered === true && " and emailed to our team"}
              {emailDelivered === false && " (the team will see it on the admin dashboard)"}.
              We typically reply within a business day to <span className="font-mono text-white">{form.email}</span>.
            </p>
            <div className="flex gap-2 mt-2">
              <button
                data-testid="contact-send-another-btn"
                onClick={() => { setSent(false); setForm({ ...form, subject: "", message: "" }); }}
                className="btn-outline text-xs"
              >
                Send another
              </button>
              <Link to="/" className="btn-accent text-xs">Back to dashboard</Link>
            </div>
          </div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="panel p-5 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Your name"
                testid="contact-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="Jane Trader"
                disabled={busy}
              />
              <Field
                label="Reply-to email"
                testid="contact-email"
                type="email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="you@example.com"
                disabled={busy}
              />
            </div>
            <Field
              label="Subject"
              testid="contact-subject"
              value={form.subject}
              onChange={(v) => update("subject", v)}
              placeholder="What's this about?"
              disabled={busy}
              maxLength={200}
            />
            <div>
              <label className="label-cap block mb-1.5">Message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us what's going on — the more detail the better."
                disabled={busy}
                rows={8}
                maxLength={5000}
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#3F3F46] outline-none px-3 py-2 text-sm text-white font-mono resize-y min-h-[160px]"
              />
              <div className="mt-1 text-[10px] text-[#71717A] font-mono text-right">
                {form.message.length}/5000
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#27272A]">
              <div className="flex items-center gap-1.5 text-[10px] text-[#71717A] font-mono">
                <Mail size={11} />
                Replies go to {form.email || "your reply-to email"}
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={busy}
                className="btn-accent flex items-center gap-1.5"
              >
                <Send size={14} />
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}

        <p className="text-[10px] text-[#71717A] leading-relaxed">
          ROLL.OPS provides educational tools only and does not give financial advice. For urgent
          trading or brokerage issues please contact your broker directly.
        </p>
      </main>
    </div>
  );
}

const Field = ({ label, testid, value, onChange, type = "text", placeholder, disabled, maxLength }) => (
  <div>
    <label className="label-cap block mb-1.5">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#3F3F46] outline-none px-3 py-2 text-sm text-white font-mono"
    />
  </div>
);
